
import PropTypes from 'prop-types';
import { useState, useEffect } from "react";
import * as Sentry from "@sentry/react";
import RangeSlider from 'react-range-slider-input';
import 'react-range-slider-input/dist/style.css';
import GrowMode from '../components/GrowMode.jsx';
import SpinnerButton from '../components/SpinnerButton.jsx';

export default function SettingsThresholds({ thresholds }) {
  const [growMode, setGrowMode] = useState({})
  const [tempValues, setTempValues] = useState([70, 80]);
  const [humidityValues, setHumidityValues] = useState([60, 100]);
  const [co2Values, setCo2Values] = useState([0, 2000]);
  const [unsaved, setUnsaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false); // TK
  const [currentThresholds, setCurrentThresholds] = useState(thresholds);

  const newTempValues = (values) => {
    setTempValues(values);
    setCustomGrowMode();
  }
  const newHumidityValues = (values) => {
    setHumidityValues(values);
    setCustomGrowMode();
  }
  const newCo2Values = (values) => {
    setCo2Values(values);
    setCustomGrowMode();
  }

  const setCustomGrowMode = () => {
    updateGrowMode({ id: 3, title: 'Custom', description: 'Set custom target values' })
  }

  const updateGrowMode = (growMode) => {
    setUnsaved(true);
    setGrowMode(growMode);
    if (growMode.title === "Spawning") {
      setTempValues([70, 80]);
      setHumidityValues([60, 100]);
      setCo2Values([6000, 10000]);
    } else if (growMode.title === "Fruiting") {
      setTempValues([70, 80]);
      setHumidityValues([60, 100]);
      setCo2Values([0, 2000]);
    }
  }

  const resetSettings = () => {
    setTempValues([currentThresholds.thresholds.temperature.low_value, currentThresholds.thresholds.temperature.high_value]);
    setHumidityValues([currentThresholds.thresholds.humidity.low_value, currentThresholds.thresholds.humidity.high_value]);
    setCo2Values([currentThresholds.thresholds.co2.low_value, currentThresholds.thresholds.co2.high_value]);
    if (currentThresholds.mode === "spawning") {
      setGrowMode({ id: 1});
    } else if (currentThresholds.mode === "fruiting") {
      setGrowMode({ id: 2});
    } else {
      setGrowMode({ id: 3})
    }
    setUnsaved(false);
  }

  const saveSettings = async () => {
    setLoading(true);

    var newThresholds = {
      mode: growMode.title.toLowerCase(),
      thresholds: {
        temperature: {
          low_value: tempValues[0],
          high_value: tempValues[1]
        },
        humidity: {
          low_value: humidityValues[0],
          high_value: humidityValues[1]
        },
        co2: {
          low_value: co2Values[0],
          high_value: co2Values[1]
        }
      },
    }
    // DO POST HERE:
    try {
      const apiBaseURL = import.meta.env.VITE_API_BASE_URL;
      const response = await fetch(apiBaseURL + "thresholds", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(newThresholds),
      });
      const connectReponse = await response.text();
      if (response.status !== 200) {
        setError(connectReponse);
        return
      }
    } catch (error) {
      Sentry.captureException(error);
      setError(error);
    }
    setCurrentThresholds(newThresholds)

    setUnsaved(false);
    setLoading(false);
  }

  useEffect(() => {
    setCurrentThresholds(thresholds)
    resetSettings()
  }, []);

  return (
    <div className="container w-full rounded-xl bg-white shadow px-4 py-8 min-h-40">
      <div className="flex items-start justify-between">
        <h2 className="text-cyan-800 text-2xl font-semibold sm:py-2 ">
          Sensor targets
        </h2>
        <div>
          <GrowMode setGrowMode={updateGrowMode} currentGrowMode={growMode} />
        </div>
      </div>
      <div className="flex">
        <div className="flex-1 ">
          <span className="">
            Temperature:
          </span>
          <RangeSlider
            min={40}
            max={100}
            value={tempValues}
            onInput={newTempValues}
            className="range-slider-temp range-slider-tall"
          />
        </div>
        <div className="w-[200px] p-4">
          {tempValues[0]} → {tempValues[1]} °F
        </div>
      </div>
      <div className="flex">
        <div className="flex-1 ">
          Humidity: <RangeSlider
            min={0}
            max={100}
            value={humidityValues}
            onInput={newHumidityValues}
            className="range-slider-humidity"
          />
        </div>
        <div className="w-[200px] p-4">
          {humidityValues[0]} → {humidityValues[1]} %RH
        </div>
      </div>
      <div className="flex">
        <div className="flex-1 ">
          CO2: <RangeSlider
            min={0}
            max={12000}
            step={100}
            value={co2Values}
            onInput={newCo2Values}
            className="range-slider-co2"
          />
        </div>
        <div className="w-[200px] p-4">
          {co2Values[0]} → {co2Values[1]} PPM
        </div>
      </div>
      {unsaved &&
        <div className="mt-4">
          {loading ? (
            <SpinnerButton />
          ) : (<>
            <button
              className="bg-cyan-500 hover:bg-cyan-700 px-4 py-2 rounded text-white font-bold uppercase tracking-wide"
              onClick={saveSettings}>
              Save
            </button>
            <button
              className="ml-2 bg-orange-500 hover:bg-orange-700 px-4 py-2 rounded text-white font-bold uppercase tracking-wide"
              onClick={resetSettings}>
              Discard
            </button>
          </>
          )}
        </div>
      }

    </div>
  );
};

SettingsThresholds.propTypes = {
  thresholds: PropTypes.object.isRequired, 
};

